import { z } from "zod";

export const TagTypeSchema = z.enum(["branch", "department", "service"]);
export const TagStatusSchema = z.boolean();
export const RoutingPermissionSchema = z.enum(["within", "across"]);
export const ServiceVisibilitySchema = z.enum(["visible", "hidden"]);
export const WeekDaySchema = z.enum([
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
]);

export const DailyScheduleSchema = z
  .object({
    day: WeekDaySchema,
    opening_time: z.string().min(1),
    break_start_time: z.string().nullable().optional(),
    break_end_time: z.string().nullable().optional(),
    ticket_end_time: z.string().min(1),
    closing_time: z.string().min(1),
  })
  .superRefine((value, ctx) => {
    if (value.opening_time >= value.closing_time) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["closing_time"],
        message: "Closing time must be after opening time",
      });
    }
    if (value.ticket_end_time <= value.opening_time) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["ticket_end_time"],
        message: "Ticket end time must be after opening time",
      });
    }
    if (value.ticket_end_time > value.closing_time) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["ticket_end_time"],
        message: "Ticket end time must be before closing time",
      });
    }
  });

export const BranchMetadataSchema = z.object({
  branch_manager_id: z.string().nullable().optional(),
  routing_permission: RoutingPermissionSchema,
});

export const DepartmentMetadataSchema = z.object({
  logo: z.string().nullable().optional(),
  re_routing: RoutingPermissionSchema,
  description: z.string().nullable().optional(),
  coordinator_id: z.string().nullable().optional(),
  contact_number: z.string().nullable().optional(),
});

export const ServiceMetadataSchema = z
  .object({
    lead_id: z.string().nullable().optional(),
    country_code: z.string().max(5).default("+92"),
    contact_number: z.string().nullable().optional(),
    average_processing_time: z.number().int().gt(0),
    max_tickets: z.number().int().gt(0).nullable().optional(),
    visibility: ServiceVisibilitySchema.default("visible"),
    logo: z.string().nullable().optional(),
    form_id: z.number().int().nullable().optional(),
    days_of_week: z.array(WeekDaySchema),
    apply_time_to_all_days: z.boolean().default(false),
    schedules: z.array(DailyScheduleSchema).min(1),
    tags: z.array(z.string()).nullable().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.days_of_week.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["days_of_week"],
        message: "Select at least one day",
      });
    }

    const scheduleDays = value.schedules.map((item) => item.day);
    const missingInSchedule = value.days_of_week.filter(
      (day) => !scheduleDays.includes(day)
    );
    if (missingInSchedule.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["schedules"],
        message: "Each selected day must have a schedule",
      });
    }
  });

const TagCommonSchema = z.object({
  org_id: z.number().int(),
  parent_tag_id: z.number().int().nullable().optional(),
  name: z.string().min(1),
  code: z.string().min(1),
  tag_status: TagStatusSchema.default(true),
  event_id: z.number().int().nullable().optional(),
});

const BranchTagRequestSchema = TagCommonSchema.extend({
  type: z.literal("branch"),
  metadata: BranchMetadataSchema,
});

const DepartmentTagRequestSchema = TagCommonSchema.extend({
  type: z.literal("department"),
  metadata: DepartmentMetadataSchema,
});

const ServiceTagRequestSchema = TagCommonSchema.extend({
  type: z.literal("service"),
  metadata: ServiceMetadataSchema,
});

export const CreateTagRequestSchema = z
  .discriminatedUnion("type", [
    BranchTagRequestSchema,
    DepartmentTagRequestSchema,
    ServiceTagRequestSchema,
  ])
  .superRefine((data, ctx) => {
    if (
      data.type === "branch" &&
      data.parent_tag_id !== null &&
      data.parent_tag_id !== undefined
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["parent_tag_id"],
        message: "Branch tags cannot have a parent",
      });
    }

    if (
      (data.type === "department" || data.type === "service") &&
      (data.parent_tag_id === null || data.parent_tag_id === undefined)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["parent_tag_id"],
        message: `Parent tag is required for ${data.type} tags`,
      });
    }
  });
